import React from "react";
import { Inbox, CalendarDays, Mail, LogOut, RefreshCw, Package, MessageSquareQuote, ImagePlus, Sparkles, LayoutDashboard, Settings, Boxes, Activity, KeyRound, PencilRuler } from "lucide-react";
import Seo from "../components/Seo";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useAdminData } from "../components/admin/useAdminData";
import { AdminLogin } from "../components/admin/AdminLogin";
import { StaffView } from "../components/admin/StaffView";
import { MaintenanceToggle } from "../components/admin/MaintenanceToggle";
import { OverviewTab } from "../components/admin/OverviewTab";
import { InventoryTab } from "../components/admin/InventoryTab";
import { StatsGrid } from "../components/admin/StatsGrid";
import { InquiriesTab } from "../components/admin/InquiriesTab";
import { BookingsTab } from "../components/admin/BookingsTab";
import { SubscribersTab } from "../components/admin/SubscribersTab";
import { ProductsTab } from "../components/admin/ProductsTab";
import { ReviewsTab } from "../components/admin/ReviewsTab";
import { MediaTab } from "../components/admin/MediaTab";
import { SeoTab } from "../components/admin/SeoTab";
import { SettingsTab } from "../components/admin/SettingsTab";
import { AccessTab } from "../components/admin/AccessTab";
import { TrafficTab } from "../components/admin/TrafficTab";
import { SiteEditorTab } from "../components/admin/SiteEditorTab";

export default function Admin() {
  const {
    token, role, setAuthState, logout, loadAll, loading,
    stats,
    inquiries, setInquiries,
    bookings, setBookings,
    subscribers,
    syncInfo, setSyncInfo,
    featured, setFeatured,
    reviews, setReviews,
    media, setMedia,
  } = useAdminData();

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  if (!token) return <AdminLogin onAuth={setAuthState} />;
  if (role === "staff") return <StaffView token={token} onLogout={logout} />;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-10 pb-10">
      <Seo title="Admin Dashboard" noindex />
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <p className="section-eyebrow">Regal Jewels</p>
          <h1 className="font-heading text-3xl sm:text-4xl text-[#171310]">Admin Dashboard</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <MaintenanceToggle auth={auth} />
          <Button variant="outline" onClick={() => loadAll(token)} disabled={loading} className="rounded-none border-[#d8cdb5]" data-testid="admin-refresh-btn">
            <RefreshCw size={15} className={`mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button variant="outline" onClick={logout} className="rounded-none border-[#d8cdb5]" data-testid="admin-logout-btn">
            <LogOut size={15} className="mr-2" /> Logout
          </Button>
        </div>
      </div>

      <StatsGrid stats={stats} />

      <Tabs defaultValue="overview">
        <TabsList className="rounded-none bg-[#f4eddc] h-auto flex-wrap justify-start">
          <TabsTrigger value="overview" className="rounded-none data-[state=active]:bg-[#171310] data-[state=active]:text-[#e9d9ac] px-5" data-testid="admin-overview-tab"><LayoutDashboard size={15} className="mr-2" /> Overview</TabsTrigger>
          <TabsTrigger value="traffic" className="rounded-none data-[state=active]:bg-[#171310] data-[state=active]:text-[#e9d9ac] px-5" data-testid="admin-traffic-tab"><Activity size={15} className="mr-2" /> Traffic</TabsTrigger>
          <TabsTrigger value="inventory" className="rounded-none data-[state=active]:bg-[#171310] data-[state=active]:text-[#e9d9ac] px-5" data-testid="admin-inventory-tab"><Boxes size={15} className="mr-2" /> Inventory</TabsTrigger>
          <TabsTrigger value="site-editor" className="rounded-none data-[state=active]:bg-[#171310] data-[state=active]:text-[#e9d9ac] px-5" data-testid="admin-site-editor-tab"><PencilRuler size={15} className="mr-2" /> Site Editor</TabsTrigger>
          <TabsTrigger value="inquiries" className="rounded-none data-[state=active]:bg-[#171310] data-[state=active]:text-[#e9d9ac] px-5"><Inbox size={15} className="mr-2" /> Inquiries ({inquiries.length})</TabsTrigger>
          <TabsTrigger value="bookings" className="rounded-none data-[state=active]:bg-[#171310] data-[state=active]:text-[#e9d9ac] px-5"><CalendarDays size={15} className="mr-2" /> Bookings ({bookings.length})</TabsTrigger>
          <TabsTrigger value="subscribers" className="rounded-none data-[state=active]:bg-[#171310] data-[state=active]:text-[#e9d9ac] px-5"><Mail size={15} className="mr-2" /> Subscribers ({subscribers.length})</TabsTrigger>
          <TabsTrigger value="products" className="rounded-none data-[state=active]:bg-[#171310] data-[state=active]:text-[#e9d9ac] px-5"><Package size={15} className="mr-2" /> Products</TabsTrigger>
          <TabsTrigger value="reviews" className="rounded-none data-[state=active]:bg-[#171310] data-[state=active]:text-[#e9d9ac] px-5"><MessageSquareQuote size={15} className="mr-2" /> Reviews ({reviews.length})</TabsTrigger>
          <TabsTrigger value="media" className="rounded-none data-[state=active]:bg-[#171310] data-[state=active]:text-[#e9d9ac] px-5" data-testid="admin-media-tab"><ImagePlus size={15} className="mr-2" /> Media ({media.length})</TabsTrigger>
          <TabsTrigger value="seo" className="rounded-none data-[state=active]:bg-[#171310] data-[state=active]:text-[#e9d9ac] px-5" data-testid="admin-seo-tab"><Sparkles size={15} className="mr-2" /> SEO</TabsTrigger>
          <TabsTrigger value="access" className="rounded-none data-[state=active]:bg-[#171310] data-[state=active]:text-[#e9d9ac] px-5" data-testid="admin-access-tab"><KeyRound size={15} className="mr-2" /> Access</TabsTrigger>
          <TabsTrigger value="settings" className="rounded-none data-[state=active]:bg-[#171310] data-[state=active]:text-[#e9d9ac] px-5" data-testid="admin-settings-tab"><Settings size={15} className="mr-2" /> Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <OverviewTab auth={auth} />
        </TabsContent>
        <TabsContent value="traffic" className="mt-6">
          <TrafficTab auth={auth} />
        </TabsContent>
        <TabsContent value="inventory" className="mt-6">
          <InventoryTab auth={auth} />
        </TabsContent>
        <TabsContent value="site-editor" className="mt-6">
          <SiteEditorTab auth={auth} />
        </TabsContent>
        <TabsContent value="inquiries" className="mt-6">
          <InquiriesTab inquiries={inquiries} setInquiries={setInquiries} auth={auth} />
        </TabsContent>
        <TabsContent value="bookings" className="mt-6">
          <BookingsTab bookings={bookings} setBookings={setBookings} auth={auth} />
        </TabsContent>
        <TabsContent value="subscribers" className="mt-6">
          <SubscribersTab subscribers={subscribers} />
        </TabsContent>
        <TabsContent value="products" className="mt-6">
          <ProductsTab auth={auth} syncInfo={syncInfo} setSyncInfo={setSyncInfo} featured={featured} setFeatured={setFeatured} />
        </TabsContent>
        <TabsContent value="reviews" className="mt-6">
          <ReviewsTab auth={auth} reviews={reviews} setReviews={setReviews} />
        </TabsContent>
        <TabsContent value="media" className="mt-6">
          <MediaTab auth={auth} media={media} setMedia={setMedia} />
        </TabsContent>
        <TabsContent value="seo" className="mt-6">
          <SeoTab auth={auth} />
        </TabsContent>
        <TabsContent value="access" className="mt-6">
          <AccessTab auth={auth} />
        </TabsContent>
        <TabsContent value="settings" className="mt-6">
          <SettingsTab auth={auth} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
